import { createClient } from '@/lib/supabase/client';

// ============================================================
// TYPES
// ============================================================

export interface UserProfile {
  id: string;
  email: string;
  fullName: string;
  firstName: string;
  lastName: string;
  phone: string;
  role: 'customer' | 'admin' | 'super_admin';
  kycStatus: 'pending' | 'verified' | 'unverified' | 'flagged';
  userStatus: 'active' | 'inactive' | 'suspended' | 'frozen';
  createdAt: string;
}

export interface BankAccount {
  id: string;
  userId: string;
  accountType: 'checking' | 'savings';
  accountNumber: string;
  accountStatus: 'active' | 'inactive' | 'frozen' | 'closed';
  balanceCents: number;
  availableBalanceCents: number;
  holdBalanceCents: number;
  interestRate: number;
  nickname: string;
  createdAt: string;
}

export interface Transaction {
  id: string;
  referenceNumber: string;
  description: string;
  transactionType: string;
  transactionStatus: string;
  totalAmountCents: number;
  initiatedBy: string;
  relatedAccountId: string;
  postedAt: string | null;
  createdAt: string;
  /**
   * Admin-selected effective date (YYYY-MM-DD string, date-only, no time component).
   * Present on admin-created transactions where the administrator chose a specific date.
   * NULL for customer-initiated transactions — use createdAt for those.
   * Use this field for customer-facing display and sorting; never modify createdAt.
   */
  effectiveDate: string | null;
  metadata: Record<string, any>;
}

export interface LedgerEntry {
  id: string;
  ledgerTransactionId: string;
  accountId: string | null;
  entryType: 'debit' | 'credit';
  amountCents: number;
  description: string;
  createdAt: string;
}

export interface Notification {
  id: string;
  userId: string;
  notificationType: string;
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string;
}

export interface WireTransfer {
  id: string;
  fromAccountId: string;
  beneficiaryName: string;
  beneficiaryAccount: string;
  destinationBank: string;
  destinationCountry: string;
  amountCents: number;
  feeCents: number;
  currency: string;
  purpose: string;
  referenceMemo: string;
  wireStatus: string;
  createdAt: string;
}

export interface AchTransfer {
  id: string;
  fromAccountId: string;
  beneficiaryName: string;
  beneficiaryAccount: string;
  amountCents: number;
  memo: string;
  achStatus: string;
  scheduledDate: string | null;
  createdAt: string;
}

export interface BillPayment {
  id: string;
  fromAccountId: string;
  billerName: string;
  amountCents: number;
  paymentStatus: string;
  scheduledDate: string | null;
  paymentReference: string;
  createdAt: string;
}

// ============================================================
// HELPER
// ============================================================

function formatCents(cents: number): string {
  return (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Normalize a date value from the effective_date column to a pure YYYY-MM-DD string.
 *
 * PostgreSQL DATE columns are returned by the Supabase JS client as "YYYY-MM-DD".
 * However, if the value was ever stored as a timestamptz (e.g. "2025-06-03T00:00:00+00:00"),
 * the client may return the longer form. This function extracts just the date part so that:
 *   1. Customer-facing date displays never receive a timestamptz that shifts by timezone.
 *   2. formatDate() in TransactionTable and RecentTransactionsFeed always gets a pure date.
 *
 * Returns null if the input is null/undefined/empty or not a recognizable date.
 */
function parsePureDate(value: string | null | undefined): string | null {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  if (value.length > 10 && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.substring(0, 10);
  return null;
}

/**
 * Normalize any date string to a comparable YYYY-MM-DD string.
 * Handles both pure date strings ("YYYY-MM-DD") and timestamptz strings
 * ("YYYY-MM-DDTHH:MM:SS+00:00"). Always extracts the first 10 characters.
 * Used by sortTransactionsDesc to ensure consistent comparison.
 */
function normalizeDateForSort(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  // Pure date or timestamptz — take first 10 chars (YYYY-MM-DD)
  return dateStr.substring(0, 10);
}

/**
 * Shared helper: determine whether a transaction is a credit or debit entry
 * from the customer's perspective. Used by both RecentTransactionsFeed and
 * TransactionTable so they always show the same direction for every transaction.
 *
 * Priority:
 *  1. metadata.direction  — most reliable for admin-created transactions
 *  2. transactionType     — reliable for customer-initiated transactions
 *  3. Default debit
 */
export function getTransactionEntryType(txn: Transaction): 'credit' | 'debit' {
  if (txn.metadata?.direction === 'credit') return 'credit';
  if (txn.metadata?.direction === 'debit') return 'debit';
  if (txn.transactionType === 'credit' || txn.transactionType === 'interest') return 'credit';
  if (txn.metadata?.reversal === true && txn.metadata?.direction === 'credit') return 'credit';
  return 'debit';
}

/**
 * Shared helper: get the display date string for a transaction.
 * Always prefers effectiveDate (admin-selected, YYYY-MM-DD) over createdAt.
 * Returns the raw string — callers format it as needed.
 *
 * NOTE: After migration 20261010240000, ALL transactions have effectiveDate set
 * (customer-initiated transactions get created_at::date as their effectiveDate).
 * This function is still correct for any edge cases.
 */
export function getTransactionDisplayDate(txn: Transaction): string {
  return txn.effectiveDate ?? txn.createdAt;
}

/**
 * Shared sort comparator: sort transactions by effective display date DESC,
 * then createdAt DESC as a stable tie-breaker.
 *
 * CRITICAL FIX: Normalizes both dates to YYYY-MM-DD before comparing.
 * This prevents mixed comparisons between:
 *   - effectiveDate: "2025-06-03" (pure date string)
 *   - createdAt: "2025-06-03T14:22:00+00:00" (timestamptz)
 * Without normalization, "2025-06-03" < "2025-06-03T..." lexicographically,
 * causing incorrect ordering when mixing admin-edited and customer transactions.
 *
 * After migration 20261010240000, all transactions have effectiveDate set,
 * so this comparator always uses effectiveDate for the primary sort key.
 */
export function sortTransactionsDesc(a: Transaction, b: Transaction): number {
  // Primary: effective display date DESC (normalized to YYYY-MM-DD for comparison)
  const aDateStr = normalizeDateForSort(a.effectiveDate ?? a.createdAt);
  const bDateStr = normalizeDateForSort(b.effectiveDate ?? b.createdAt);
  if (aDateStr !== bDateStr) return bDateStr.localeCompare(aDateStr); // DESC

  // Secondary: created_at DESC (stable tie-breaker for same-day transactions)
  // Use full timestamptz for sub-day precision
  return b.createdAt.localeCompare(a.createdAt);
}

/**
 * Returns true if a ledger_transactions row is an administrator-created record
 * that should be hidden from customer-facing views.
 *
 * Two sources of admin records exist:
 *
 * 1. fn_admin_create_transaction — sets metadata.admin_created = true
 *    (handled by migrations 20261010160000 and 20261010170000)
 *
 * 2. fn_apply_fund_adjustment — sets metadata.adjustment_type and metadata.admin_id
 *    but historically did NOT set admin_created or hidden_from_customer.
 *    Migration 20261010180000 backfills hidden_from_customer=true on these records
 *    and updates the function to set it going forward. This client-side check
 *    provides defense-in-depth for any records that may have been created before
 *    the migration was applied.
 *
 * The authoritative control is hidden_from_customer in metadata (enforced by RLS).
 * This function is a secondary client-side safety net only.
 */
function isAdminCreatedRecord(row: any): boolean {
  const meta = row.metadata || {};

  // Primary check: explicit hidden_from_customer flag (set by both RPCs after migrations)
  if (meta.hidden_from_customer === true) return true;

  // Secondary check: fn_apply_fund_adjustment signature
  // These records have adjustment_type + admin_id but may lack hidden_from_customer
  // if created before migration 20261010180000 was applied.
  if (
    row.transaction_type === 'admin_adjustment' &&
    meta.adjustment_type !== undefined &&
    meta.admin_id !== undefined &&
    meta.admin_created !== true // not already caught by primary check path
  ) {
    return true;
  }

  return false;
}

// ============================================================
// ACCOUNT SERVICE
// ============================================================

export const accountService = {
  async getMyAccounts(): Promise<BankAccount[]> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('bank_accounts')
      .select('*')
      .eq('user_id', user.id)
      .order('account_type', { ascending: true });

    if (error) return [];

    return (data || []).map(row => ({
      id: row.id,
      userId: row.user_id,
      accountType: row.account_type,
      accountNumber: row.account_number,
      accountStatus: row.account_status,
      balanceCents: row.balance_cents,
      availableBalanceCents: row.available_balance_cents,
      holdBalanceCents: row.hold_balance_cents,
      interestRate: row.interest_rate,
      nickname: row.nickname || '',
      createdAt: row.created_at,
    }));
  },

  async getAccountById(accountId: string): Promise<BankAccount | null> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from('bank_accounts')
      .select('*')
      .eq('id', accountId)
      .single();

    if (error) return null;

    return {
      id: data.id,
      userId: data.user_id,
      accountType: data.account_type,
      accountNumber: data.account_number,
      accountStatus: data.account_status,
      balanceCents: data.balance_cents,
      availableBalanceCents: data.available_balance_cents,
      holdBalanceCents: data.hold_balance_cents,
      interestRate: data.interest_rate,
      nickname: data.nickname || '',
      createdAt: data.created_at,
    };
  },

  async createAccountsForUser(userId: string): Promise<{ checking: BankAccount | null; savings: BankAccount | null }> {
    const supabase = createClient();

    // Generate cryptographically random 10-digit account numbers
    const randomDigits = () => {
      const buf = new Uint32Array(1);
      crypto.getRandomValues(buf);
      // Map to 10-digit range: 1000000000 – 9999999999
      return String(1000000000 + (buf[0] % 9000000000)).padStart(10, '0');
    };
    const checkingNum = `NXB-CHK-${randomDigits()}`;
    const savingsNum = `NXB-SAV-${randomDigits()}`;

    const { data: checking } = await supabase
      .from('bank_accounts')
      .insert({ user_id: userId, account_type: 'checking', account_number: checkingNum, balance_cents: 0, available_balance_cents: 0 })
      .select()
      .single();

    const { data: savings } = await supabase
      .from('bank_accounts')
      .insert({ user_id: userId, account_type: 'savings', account_number: savingsNum, balance_cents: 0, available_balance_cents: 0, interest_rate: 0.0475 })
      .select()
      .single();

    return {
      checking: checking ? { id: checking.id, userId: checking.user_id, accountType: checking.account_type, accountNumber: checking.account_number, accountStatus: checking.account_status, balanceCents: 0, availableBalanceCents: 0, holdBalanceCents: 0, interestRate: 0, nickname: '', createdAt: checking.created_at } : null,
      savings: savings ? { id: savings.id, userId: savings.user_id, accountType: savings.account_type, accountNumber: savings.account_number, accountStatus: savings.account_status, balanceCents: 0, availableBalanceCents: 0, holdBalanceCents: 0, interestRate: 0.0475, nickname: '', createdAt: savings.created_at } : null,
    };
  },
};

// ============================================================
// TRANSACTION SERVICE
// ============================================================

export const transactionService = {
  async getMyTransactions(accountId?: string, limit = 100): Promise<Transaction[]> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    let query = supabase
      .from('ledger_transactions')
      .select('*')
      // Sort by effective_date DESC (now always set after migration 20261010240000),
      // then created_at DESC as the stable secondary key.
      // NULLS LAST is kept as a safety guard for any rows that may have been
      // inserted before the trigger was created.
      .order('effective_date', { ascending: false, nullsFirst: false })
      .order('created_at', { ascending: false })
      .limit(limit);

    if (accountId) {
      query = query.eq('related_account_id', accountId);
    } else {
      const { data: accounts } = await supabase
        .from('bank_accounts').select('id').eq('user_id', user.id);

      const accountIds = (accounts || []).map((a: { id: string }) => a.id);
      if (accountIds.length === 0) return [];

      query = query.or(
        `related_account_id.in.(${accountIds.join(',')}),initiated_by.eq.${user.id}`
      );
    }

    const { data, error } = await query;
    if (error) return [];

    return (data || [])
      .filter((row: any) => !isAdminCreatedRecord(row))
      .map((row: any) => ({
        id: row.id,
        referenceNumber: row.reference_number,
        description: row.metadata?.narration?.trim()
          ? row.metadata.narration.trim()
          : row.description,
        transactionType: row.transaction_type,
        transactionStatus: row.transaction_status,
        totalAmountCents: row.total_amount_cents,
        initiatedBy: row.initiated_by,
        relatedAccountId: row.related_account_id,
        postedAt: row.posted_at,
        createdAt: row.created_at,
        // parsePureDate ensures effectiveDate is always "YYYY-MM-DD" or null.
        // After migration 20261010240000, this will always return a value.
        effectiveDate: parsePureDate(row.effective_date),
        metadata: {
          direction: row.metadata?.direction,
          currency: row.metadata?.currency,
          narration: row.metadata?.narration,
          reversal: row.metadata?.reversal,
          reversed: row.metadata?.reversed,
          original_transaction_id: row.metadata?.original_transaction_id,
        },
      }));
  },

  async getTransactionsByAccount(accountId: string, limit = 50): Promise<Transaction[]> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from('ledger_transactions').select('*').eq('related_account_id', accountId)
      .order('effective_date', { ascending: false, nullsFirst: false })
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) return [];

    return (data || [])
      .filter((row: any) => !isAdminCreatedRecord(row))
      .map((row: any) => ({
        id: row.id,
        referenceNumber: row.reference_number,
        description: row.metadata?.narration?.trim()
          ? row.metadata.narration.trim()
          : row.description,
        transactionType: row.transaction_type,
        transactionStatus: row.transaction_status,
        totalAmountCents: row.total_amount_cents,
        initiatedBy: row.initiated_by,
        relatedAccountId: row.related_account_id,
        postedAt: row.posted_at,
        createdAt: row.created_at,
        effectiveDate: parsePureDate(row.effective_date),
        metadata: {
          direction: row.metadata?.direction,
          currency: row.metadata?.currency,
          narration: row.metadata?.narration,
          reversal: row.metadata?.reversal,
          reversed: row.metadata?.reversed,
          original_transaction_id: row.metadata?.original_transaction_id,
        },
      }));
  },

  /**
   * Performs an internal transfer exclusively via the fn_internal_transfer RPC.
   * If the RPC is unavailable or fails, returns a safe error — no client-side
   * financial writes are performed as a fallback.
   */
  async performInternalTransfer(params: {
    fromAccountId: string;
    toAccountId: string;
    amountCents: number;
    description: string;
    idempotencyKey?: string;
  }): Promise<{ success: boolean; referenceNumber?: string; error?: string }> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: 'Not authenticated' };

    const { data, error } = await supabase.rpc('fn_internal_transfer', {
      p_from_account_id: params.fromAccountId,
      p_to_account_id: params.toAccountId,
      p_amount_cents: params.amountCents,
      p_description: params.description || 'Internal Transfer',
      p_idempotency_key: params.idempotencyKey || null,
    });

    if (error) {
      return { success: false, error: error.message || 'Transfer failed. Please try again.' };
    }

    const result = data as { success: boolean; referenceNumber?: string; error?: string };
    return result;
  },
};

// ============================================================
// NOTIFICATION SERVICE
// ============================================================

export const notificationService = {
  async getMyNotifications(): Promise<Notification[]> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(50);

    if (error) return [];

    return (data || [])
      // Filter out internal admin-only notifications not meant for customer display
      .filter((row: any) => row.metadata?.admin_internal !== true)
      .map(row => ({
        id: row.id,
        userId: row.user_id,
        notificationType: row.notification_type,
        title: row.title,
        message: row.message,
        isRead: row.is_read,
        createdAt: row.created_at,
      }));
  },

  async markAsRead(notificationId: string): Promise<void> {
    const supabase = createClient();
    await supabase.from('notifications').update({ is_read: true }).eq('id', notificationId);
  },

  async markAllAsRead(): Promise<void> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    await supabase.from('notifications').update({ is_read: true }).eq('user_id', user.id).eq('is_read', false);
  },

  async getUnreadCount(): Promise<number> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return 0;

    try {
      // Fetch non-internal unread notifications (count doesn't support jsonb filter directly)
      const { data } = await supabase
        .from('notifications').select('id, metadata').eq('user_id', user.id).eq('is_read', false);
      if (!data) return 0;
      // Exclude internal admin-only notifications
      return data.filter((n: any) => n.metadata?.admin_internal !== true).length;
    } catch {
      return 0;
    }
  },
};

// ============================================================
// ADMIN SERVICE
// ============================================================

export const adminService = {
  async getAllUsers(): Promise<any[]> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .neq('role', 'admin')
      .order('created_at', { ascending: false });

    if (error) return [];
    return data || [];
  },

  async getAllAccounts(): Promise<any[]> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from('bank_accounts')
      .select('*, user_profiles(full_name, email, first_name, last_name)')
      .in('account_type', ['checking', 'savings'])
      .order('created_at', { ascending: false });

    if (error) return [];
    return data || [];
  },

  /**
   * Applies a fund adjustment exclusively via the fn_apply_fund_adjustment RPC.
   * If the RPC is unavailable or fails, returns a safe error — no client-side
   * financial writes are performed as a fallback.
   */
  async applyFundAdjustment(params: {
    accountId: string;
    adjustmentType: 'credit' | 'debit';
    amountCents: number;
    reason: string;
    adminId: string;
    idempotencyKey?: string;
  }): Promise<{ success: boolean; referenceNumber?: string; error?: string }> {
    const supabase = createClient();

    const { data, error } = await supabase.rpc('fn_apply_fund_adjustment', {
      p_account_id: params.accountId,
      p_adjustment_type: params.adjustmentType,
      p_amount_cents: params.amountCents,
      p_reason: params.reason,
      p_admin_id: params.adminId,
      p_idempotency_key: params.idempotencyKey || null,
    });

    if (error) {
      return { success: false, error: error.message || 'Fund adjustment failed. Please try again.' };
    }

    const result = data as { success: boolean; referenceNumber?: string; error?: string };
    return result;
  },

  async updateUserStatus(userId: string, status: string, adminId: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ user_status: status, updated_at: new Date().toISOString() })
        .eq('id', userId);

      if (error) return false;

      await supabase.from('audit_logs').insert({
        actor_id: adminId,
        actor_email: 'admin',
        action: `user_status_change`,
        resource_type: 'user_profile',
        resource_id: userId,
        after_state: { user_status: status },
        reason: `Status changed to ${status}`,
      });

      return true;
    } catch {
      return false;
    }
  },

  async updateAccountStatus(accountId: string, status: string, adminId: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('bank_accounts')
        .update({ account_status: status, updated_at: new Date().toISOString() })
        .eq('id', accountId);

      if (error) return false;

      await supabase.from('audit_logs').insert({
        actor_id: adminId,
        actor_email: 'admin',
        action: `account_status_change`,
        resource_type: 'bank_account',
        resource_id: accountId,
        after_state: { account_status: status },
        reason: `Account status changed to ${status}`,
      });

      return true;
    } catch {
      return false;
    }
  },

  async getDashboardStats(): Promise<any> {
    const supabase = createClient();
    try {
      const [usersRes, accountsRes, txnRes, wireRes] = await Promise.all([
        supabase.from('user_profiles').select('*', { count: 'exact', head: true }).neq('role', 'admin'),
        supabase.from('bank_accounts').select('balance_cents, account_type, account_status'),
        supabase.from('ledger_transactions').select('*', { count: 'exact', head: true }),
        supabase.from('wire_transfers').select('*', { count: 'exact', head: true }).eq('wire_status', 'submitted'),
      ]);

      const accounts = accountsRes.data || [];
      const totalBalance = accounts.reduce((sum: number, a: any) => sum + (a.balance_cents || 0), 0);
      const checkingCount = accounts.filter((a: any) => a.account_type === 'checking').length;
      const savingsCount = accounts.filter((a: any) => a.account_type === 'savings').length;
      const frozenCount = accounts.filter((a: any) => a.account_status === 'frozen').length;

      return {
        totalCustomers: usersRes.count || 0,
        totalAccounts: accounts.length,
        checkingAccounts: checkingCount,
        savingsAccounts: savingsCount,
        totalBalanceCents: totalBalance,
        totalTransactions: txnRes.count || 0,
        pendingWires: wireRes.count || 0,
        frozenAccounts: frozenCount,
      };
    } catch {
      return { totalCustomers: 0, totalAccounts: 0, checkingAccounts: 0, savingsAccounts: 0, totalBalanceCents: 0, totalTransactions: 0, pendingWires: 0, frozenAccounts: 0 };
    }
  },

  async getAuditLogs(limit = 50): Promise<any[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('audit_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) return [];
      return data || [];
    } catch {
      return [];
    }
  },
};

// ============================================================
// WIRE TRANSFER SERVICE
// ============================================================

export const wireService = {
  /**
   * Submits a wire transfer exclusively via the fn_submit_wire RPC.
   * If the RPC is unavailable or fails, returns a safe error — no client-side
   * financial writes are performed as a fallback.
   */
  async submitWireTransfer(params: {
    fromAccountId: string;
    beneficiaryName: string;
    beneficiaryAccount: string;
    destinationBank: string;
    destinationCountry: string;
    amountCents: number;
    feeCents: number;
    currency: string;
    purpose: string;
    referenceMemo: string;
    idempotencyKey?: string;
  }): Promise<{ success: boolean; wireId?: string; referenceNumber?: string; error?: string }> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: 'Not authenticated' };

    const { data, error } = await supabase.rpc('fn_submit_wire', {
      p_from_account_id: params.fromAccountId,
      p_beneficiary_name: params.beneficiaryName,
      p_beneficiary_account: params.beneficiaryAccount,
      p_destination_bank: params.destinationBank,
      p_destination_country: params.destinationCountry,
      p_amount_cents: params.amountCents,
      p_fee_cents: params.feeCents,
      p_currency: params.currency,
      p_purpose: params.purpose,
      p_reference_memo: params.referenceMemo,
      p_idempotency_key: params.idempotencyKey || null,
    });

    if (error) {
      return { success: false, error: error.message || 'Wire transfer failed. Please try again.' };
    }

    const result = data as { success: boolean; wireId?: string; referenceNumber?: string; error?: string };
    return result;
  },

  async getMyWireTransfers(): Promise<WireTransfer[]> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('wire_transfers')
      .select('*')
      .eq('initiated_by', user.id)
      .order('created_at', { ascending: false });

    if (error) return [];
    return (data || []).map(row => ({
      id: row.id,
      fromAccountId: row.from_account_id,
      beneficiaryName: row.beneficiary_name,
      beneficiaryAccount: row.beneficiary_account,
      destinationBank: row.destination_bank,
      destinationCountry: row.destination_country,
      amountCents: row.amount_cents,
      feeCents: row.fee_cents,
      currency: row.currency,
      purpose: row.purpose,
      referenceMemo: row.reference_memo,
      wireStatus: row.wire_status,
      createdAt: row.created_at,
    }));
  },

  async getAllWireTransfers(): Promise<any[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('wire_transfers')
        .select('*, user_profiles!wire_transfers_initiated_by_fkey(full_name, email), bank_accounts!wire_transfers_from_account_id_fkey(account_number)')
        .order('created_at', { ascending: false });

      if (error) return [];
      return data || [];
    } catch {
      return [];
    }
  },

  /**
   * Updates wire status exclusively via the fn_update_wire_status RPC.
   * If the RPC is unavailable or fails, returns false — no client-side
   * financial writes are performed as a fallback.
   */
  async updateWireStatus(wireId: string, status: string, adminId: string, rejectionReason?: string): Promise<boolean> {
    const supabase = createClient();

    const { data, error } = await supabase.rpc('fn_update_wire_status', {
      p_wire_id: wireId,
      p_new_status: status,
      p_admin_id: adminId,
      p_rejection_reason: rejectionReason || null,
    });

    if (error) return false;

    const result = data as { success: boolean; error?: string };
    return result.success;
  },
};

// ============================================================
// ACH SERVICE
// ============================================================

export const achService = {
  async submitAchTransfer(params: {
    fromAccountId: string;
    beneficiaryName: string;
    beneficiaryAccount: string;
    beneficiaryRouting?: string;
    amountCents: number;
    memo: string;
    scheduledDate?: string;
    idempotencyKey?: string;
  }): Promise<{ success: boolean; achId?: string; error?: string }> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: 'Not authenticated' };

    try {
      // Verify account ownership and sufficient funds
      const { data: account, error: acctErr } = await supabase
        .from('bank_accounts')
        .select('available_balance_cents, hold_balance_cents, account_status, user_id')
        .eq('id', params.fromAccountId)
        .eq('user_id', user.id)
        .single();

      if (acctErr || !account) return { success: false, error: 'Account not found or unauthorized' };
      if (account.account_status !== 'active') return { success: false, error: 'Account is not active' };
      if (account.available_balance_cents < params.amountCents) {
        return { success: false, error: 'Insufficient funds' };
      }

      // Insert ACH transfer record
      const { data: ach, error: achErr } = await supabase
        .from('ach_transfers')
        .insert({
          from_account_id: params.fromAccountId,
          beneficiary_name: params.beneficiaryName,
          beneficiary_account: params.beneficiaryAccount,
          amount_cents: params.amountCents,
          memo: params.memo || '',
          ach_status: 'pending',
          scheduled_date: params.scheduledDate || null,
          initiated_by: user.id,
        })
        .select()
        .single();

      if (achErr) throw achErr;

      // Place hold on available balance
      const { error: holdErr } = await supabase
        .from('bank_accounts')
        .update({
          hold_balance_cents: (account.hold_balance_cents || 0) + params.amountCents,
          available_balance_cents: account.available_balance_cents - params.amountCents,
          updated_at: new Date().toISOString(),
        })
        .eq('id', params.fromAccountId)
        .eq('user_id', user.id);

      if (holdErr) {
        // Rollback ACH record if hold fails
        await supabase.from('ach_transfers').delete().eq('id', ach.id);
        throw holdErr;
      }

      // Notify customer
      await supabase.from('notifications').insert({
        user_id: user.id,
        notification_type: 'ach',
        title: 'ACH Transfer Submitted',
        message: `Your ACH transfer of $${formatCents(params.amountCents)} to ${params.beneficiaryName} is pending processing. Funds have been held.`,
      });

      return { success: true, achId: ach.id };
    } catch (err: any) {
      return { success: false, error: err.message || 'ACH transfer failed' };
    }
  },

  async getMyAchTransfers(): Promise<AchTransfer[]> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('ach_transfers')
      .select('*')
      .eq('initiated_by', user.id)
      .order('created_at', { ascending: false });

    if (error) return [];
    return (data || []).map(row => ({
      id: row.id,
      fromAccountId: row.from_account_id,
      beneficiaryName: row.beneficiary_name,
      beneficiaryAccount: row.beneficiary_account,
      amountCents: row.amount_cents,
      memo: row.memo,
      achStatus: row.ach_status,
      scheduledDate: row.scheduled_date,
      createdAt: row.created_at,
    }));
  },
};

// ============================================================
// BILL PAYMENT SERVICE
// ============================================================

export const billPayService = {
  async getMyBillers(): Promise<any[]> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('billers')
      .select('*')
      .eq('user_id', user.id)
      .eq('is_active', true)
      .order('biller_name');

    if (error) return [];
    return data || [];
  },

  async addBiller(params: { billerName: string; category: string; accountNumber: string }): Promise<boolean> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    try {
      const { error } = await supabase.from('billers').insert({
        user_id: user.id,
        biller_name: params.billerName,
        category: params.category,
        account_number: params.accountNumber,
      });
      return !error;
    } catch {
      return false;
    }
  },

  /**
   * Pays a bill exclusively via the fn_pay_bill RPC.
   * If the RPC is unavailable or fails, returns a safe error — no client-side
   * financial writes are performed as a fallback.
   */
  async payBill(params: {
    fromAccountId: string;
    billerName: string;
    billerId?: string;
    amountCents: number;
    scheduledDate?: string;
    idempotencyKey?: string;
  }): Promise<{ success: boolean; paymentReference?: string; error?: string }> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: 'Not authenticated' };

    const { data, error } = await supabase.rpc('fn_pay_bill', {
      p_from_account_id: params.fromAccountId,
      p_biller_name: params.billerName,
      p_amount_cents: params.amountCents,
      p_biller_id: params.billerId || null,
      p_scheduled_date: params.scheduledDate || null,
      p_idempotency_key: params.idempotencyKey || null,
    });

    if (error) {
      return { success: false, error: error.message || 'Bill payment failed. Please try again.' };
    }

    const result = data as { success: boolean; paymentReference?: string; error?: string };
    return result;
  },

  async getMyBillPayments(): Promise<BillPayment[]> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('bill_payments')
      .select('*')
      .eq('initiated_by', user.id)
      .order('created_at', { ascending: false });

    if (error) return [];
    return (data || []).map(row => ({
      id: row.id,
      fromAccountId: row.from_account_id,
      billerName: row.biller_name,
      amountCents: row.amount_cents,
      paymentStatus: row.payment_status,
      scheduledDate: row.scheduled_date,
      paymentReference: row.payment_reference,
      createdAt: row.created_at,
    }));
  },
};

// ============================================================
// EMAIL SERVICE
// ============================================================

export const emailService = {
  async sendEmail(params: {
    to: string;
    subject: string;
    html: string;
    emailType: string;
    userId?: string;
    relatedTransactionId?: string;
  }): Promise<boolean> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase.functions.invoke('send-email', {
        body: {
          to: params.to,
          subject: params.subject,
          html: params.html,
          emailType: params.emailType,
        },
      });

      const status = error ? 'failed' : 'sent';
      await supabase.from('email_records').insert({
        user_id: params.userId || null,
        recipient_email: params.to,
        email_type: params.emailType,
        subject: params.subject,
        status,
        provider_id: data?.id || '',
        error_message: error?.message || '',
        related_transaction_id: params.relatedTransactionId || null,
        sent_at: status === 'sent' ? new Date().toISOString() : null,
      });

      return !error;
    } catch {
      return false;
    }
  },

  buildTransactionEmail(params: {
    customerName: string;
    transactionType: string;
    amount: string;
    reference: string;
    status: string;
    description: string;
    date: string;
  }): string {
    return `
      <h2 style="color:#0F2D5E;margin:0 0 16px;font-size:20px;">Transaction ${params.transactionType}</h2>
      <p style="color:#475569;margin:0 0 24px;">Hello ${params.customerName},</p>
      <div style="background:#F8FAFC;border-radius:8px;padding:20px;margin-bottom:24px;">
        <table style="width:100%;border-collapse:collapse;">
          <tr><td style="padding:8px 0;color:#64748B;font-size:14px;">Transaction Type</td><td style="padding:8px 0;color:#0F172A;font-size:14px;font-weight:600;text-align:right;">${params.transactionType}</td></tr>
          <tr><td style="padding:8px 0;color:#64748B;font-size:14px;">Amount</td><td style="padding:8px 0;color:#0F172A;font-size:14px;font-weight:700;text-align:right;">${params.amount}</td></tr>
          <tr><td style="padding:8px 0;color:#64748B;font-size:14px;">Reference</td><td style="padding:8px 0;color:#0F172A;font-size:14px;font-family:monospace;text-align:right;">${params.reference}</td></tr>
          <tr><td style="padding:8px 0;color:#64748B;font-size:14px;">Status</td><td style="padding:8px 0;color:#059669;font-size:14px;font-weight:600;text-align:right;">${params.status}</td></tr>
          <tr><td style="padding:8px 0;color:#64748B;font-size:14px;">Date</td><td style="padding:8px 0;color:#0F172A;font-size:14px;text-align:right;">${params.date}</td></tr>
          <tr><td style="padding:8px 0;color:#64748B;font-size:14px;">Description</td><td style="padding:8px 0;color:#0F172A;font-size:14px;text-align:right;">${params.description}</td></tr>
        </table>
      </div>
      <p style="color:#94A3B8;font-size:13px;">If you have questions, contact Capital City BNT Support.</p>
    `;
  },
};

export { formatCents };
